import type { MessageDescriptor } from 'react-intl';

import { fireErrorAnalytics } from '@atlassian/ui-modifications-analytics';

import type { ViewType } from '../../common/types/context';
import type { ExecutionContext } from '../../common/types/execution-context';
import type { FieldType } from '../../common/types/field';
import type { AnalyticsResult, BaseFieldConfiguration } from '../../types';

import { getViewConfiguration } from './store';

const isKeyOf = <T extends Record<string, unknown>>(key: keyof T, object: T): key is keyof T =>
	object[key] !== undefined;

const noop = () => {};

/**
 * Returned by `getAnalytics` when the requested view type has no registered
 * configuration yet and the guard gate is on, so callers (e.g. `useSubmit`)
 * keep working instead of throwing while configs register lazily.
 */
const UNSUPPORTED_VIEW_TYPE_FALLBACK_ANALYTICS: AnalyticsResult = {
	stopTTI: noop,
	cancelTTI: noop,
	markIframeLoad: noop,
	markFieldsRegistered: noop,
	markOnInitResolved: noop,
};

const getConfigurationByViewType = <T extends BaseFieldConfiguration = BaseFieldConfiguration>({
	viewType,
}: {
	viewType: ViewType;
}) => {
	const viewConfiguration = getViewConfiguration<T>(viewType);

	if (!viewConfiguration) {
		throw new Error(`Unsupported view type ${viewType} in getConfigurationByViewType.`);
	}

	return viewConfiguration;
};

/** Non-throwing check for whether a configuration is registered for the view type. */
export function isViewTypeSupported(viewType: ViewType): boolean {
	return getViewConfiguration(viewType) !== undefined;
}

export function getSupportedFieldConfiguration<T extends BaseFieldConfiguration>(
	viewType: ViewType,
	fieldType: FieldType,
): T | undefined {
	// View configurations register lazily; until the requested view type is
	// registered, treat the field as unsupported instead of throwing.
	if (!isViewTypeSupported(viewType)) {
		return undefined;
	}

	const fieldsConfiguration = getConfigurationByViewType<T>({
		viewType,
	}).getSupportedFieldsConfiguration();

	if (!isKeyOf(fieldType, fieldsConfiguration)) {
		fireErrorAnalytics({
			error: new Error('Unsupported field type.'),
			id: 'getSupportedFieldConfiguration',
			viewType,
			attributes: {
				fieldType,
			},
		});

		return undefined;
	}

	return fieldsConfiguration[fieldType];
}

export const getSupportedFieldTypes = (viewType: ViewType): string[] => {
	if (!isViewTypeSupported(viewType)) {
		return [];
	}

	return getConfigurationByViewType({ viewType }).getSupportedFieldTypes();
};

export function isFieldTypeSupported(
	viewType: ViewType,
	fieldType: FieldType | undefined,
): fieldType is FieldType {
	return (
		fieldType !== undefined &&
		// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
		(getSupportedFieldTypes(viewType) as string[]).includes(fieldType)
	);
}

export function getConsentMessage(viewType: ViewType): MessageDescriptor {
	return getConfigurationByViewType({ viewType }).getConsentMessage();
}

export function isExecutionContextSupported(context: ExecutionContext): boolean {
	if (!isViewTypeSupported(context.viewType)) {
		return false;
	}

	return getConfigurationByViewType({ viewType: context.viewType }).isExecutionContextSupported({
		context,
	});
}

export function getAnalytics(viewType: ViewType): AnalyticsResult {
	if (!isViewTypeSupported(viewType)) {
		return UNSUPPORTED_VIEW_TYPE_FALLBACK_ANALYTICS;
	}

	return getConfigurationByViewType({ viewType }).getAnalytics();
}

export function getSupportedTriggerPointsConfiguration(viewType: ViewType): Set<string> {
	if (!isViewTypeSupported(viewType)) {
		return new Set();
	}

	return getConfigurationByViewType({ viewType }).getSupportedTriggerPointsConfiguration();
}

export const isTriggerPointSupported = (
	viewType: ViewType,
	triggerPoint: string | undefined,
): boolean =>
	triggerPoint !== undefined && getSupportedTriggerPointsConfiguration(viewType).has(triggerPoint);
